import { STATUS_LISTING_CHIP } from '@/constants/app/marketing';
import { useAppSelector } from '@/store';
import { useBulkMarketingActionMutation } from '@/store/api/services/marketing/marketing';
import type { ListingView } from '@/types';
import { authReducer } from '@bitstopco/bitstop-theme';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo } from 'react';
import toast from 'react-hot-toast';

const {
  selectors: { userInfo },
} = authReducer;

function ineligibleReason(item: ListingView, action: 'publish' | 'unpublish'): string | null {
  if (action === 'publish') {
    if (item.marketing_status === 'published') return 'Already published';
    if (item.marketing_status === 'in_review') return 'Awaiting review';
    if (!item.atm_ids?.length) return 'No machine installed';
    return null;
  }
  if (item.marketing_status === 'unpublished') return 'Already unpublished';
  return null;
}

const BulkMarketingListingAction = ({ hide, props }: ModalChildrenProps) => {
  const action = props?.data?.action as 'publish' | 'unpublish';
  const selectedItems = (props?.data?.selectedItems ?? []) as ListingView[];
  const [bulkAction, { isLoading, isSuccess, data: result }] = useBulkMarketingActionMutation();
  const userData = useAppSelector(userInfo);

  const isPublish = action === 'publish';
  const label = isPublish ? 'Publish' : 'Unpublish';
  const apiAction = isPublish ? 'AskForReview' : 'Unpublish';

  const nameParts = [userData?.first_name, userData?.last_name].filter(Boolean);
  const user = nameParts.length > 0 ? nameParts.join(' ') : (userData?.email ?? '');

  const { eligible, ineligible } = useMemo(() => {
    const eligible: ListingView[] = [];
    const ineligible: { item: ListingView; reason: string }[] = [];
    for (const item of selectedItems) {
      const reason = ineligibleReason(item, action);
      if (reason) ineligible.push({ item, reason });
      else eligible.push(item);
    }
    return { eligible, ineligible };
  }, [selectedItems, action]);

  const handleSubmit = async () => {
    try {
      await bulkAction({
        location_ids: eligible.map((i) => i.location_id),
        action: apiAction,
        user,
      }).unwrap();
      toast.success(`Bulk ${label.toLowerCase()} completed.`);
    } catch (_err) {
      toast.error(`Failed to ${label.toLowerCase()} listings.`);
    }
  };

  return (
    <>
      <DialogContent sx={{ py: 2 }}>
        {!isSuccess ? (
          <>
            <Typography variant="h6" mb={1}>
              {label} {eligible.length} listing(s)?
            </Typography>
            {ineligible.length > 0 && (
              <Typography variant="body2" color="text.secondary" mb={2}>
                {ineligible.length} selected listing(s) will be skipped — hover a row to see why.
              </Typography>
            )}
            <Divider />
            <TableContainer
              sx={{ mt: 2, opacity: isLoading ? 0.5 : 1, transition: 'opacity 0.3s' }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Location</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {eligible.map((item) => (
                    <TableRow key={item.location_id}>
                      <TableCell>{item.name ?? `Location ${item.location_id}`}</TableCell>
                      <TableCell>
                        {(() => {
                          const chip = STATUS_LISTING_CHIP[item.marketing_status] ?? {
                            label: item.marketing_status,
                            color: 'default' as const,
                          };
                          return (
                            <Chip
                              size="small"
                              color={chip.color}
                              label={chip.label}
                              sx={{ fontWeight: 'bold', fontSize: '11px' }}
                            />
                          );
                        })()}
                      </TableCell>
                    </TableRow>
                  ))}
                  {ineligible.map(({ item, reason }) => (
                    <Tooltip key={item.location_id} title={reason} placement="left">
                      <TableRow sx={{ opacity: 0.4 }}>
                        <TableCell sx={{ textDecoration: 'line-through' }}>
                          {item.name ?? `Location ${item.location_id}`}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.disabled">
                            {reason}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </Tooltip>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <>
            <Typography variant="h6" mb={1}>
              {label} complete
            </Typography>
            <Box sx={{ display: 'flex', gap: 4, mt: 1, mb: 2 }}>
              <Box>
                <Typography variant="h4" fontWeight={700} color="success.main">
                  {result?.data?.processed_count ?? 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Succeeded
                </Typography>
              </Box>
              {(result?.data?.skipped_count ?? 0) > 0 && (
                <Box>
                  <Typography variant="h4" fontWeight={700} color="warning.main">
                    {result?.data?.skipped_count}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Skipped
                  </Typography>
                </Box>
              )}
            </Box>
            {(result?.data?.items?.length ?? 0) > 0 && (
              <>
                <Divider />
                <TableContainer sx={{ mt: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Location</TableCell>
                        <TableCell>New Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result?.data?.items?.map((item) => (
                        <TableRow key={item.location_id}>
                          <TableCell>{item.name ?? `Location ${item.location_id}`}</TableCell>
                          <TableCell>
                            {(() => {
                              const chip = STATUS_LISTING_CHIP[item.marketing_status] ?? {
                                label: item.marketing_status,
                                color: 'default' as const,
                              };
                              return (
                                <Chip
                                  size="small"
                                  color={chip.color}
                                  label={chip.label}
                                  sx={{ fontWeight: 'bold', fontSize: '11px' }}
                                />
                              );
                            })()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
            {(result?.data?.skipped_count ?? 0) > 0 && (
              <Typography variant="caption" color="text.disabled" display="block" mt={1}>
                Skipped listings did not meet eligibility requirements.
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        {!isSuccess ? (
          <>
            <Button onClick={hide}>Cancel</Button>
            <LoadingButton
              variant="contained"
              color={isPublish ? 'primary' : 'error'}
              onClick={handleSubmit}
              loading={isLoading}
              disabled={eligible.length === 0}
            >
              {label} {eligible.length > 0 ? eligible.length : ''} listing(s)
            </LoadingButton>
          </>
        ) : (
          <Button fullWidth onClick={hide}>
            Done
          </Button>
        )}
      </DialogActions>
    </>
  );
};

export default BulkMarketingListingAction;
